import {
  CallTransition,
  type CallStateMachine,
} from '../../features/call/call.types';
import { type ServiceTokens } from '../../container';
import { SessionEventType, type Session } from '../../webrtc/session';
import {
  type AspectRatio,
  type Media,
  type VideoQuality,
} from '../../webrtc/media';
import { PluggableService } from '../pluggableService/pluggableService';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { type Subscription } from 'xstate';
import { type Observable } from 'rxjs/internal/Observable';
import { type Logger } from 'loglevel';
import {
  type CallStateSubject,
  type CallService,
  ManualVideoQuality,
} from './interface';
import { type CommonRealtimeService } from './realtime/commonRealtime.service';
import {
  type SupportedImageTypes,
  getArrayBufferFromVideoElement,
} from '../../webrtc/image';
import { StatsToLog } from '../../webrtc/session/metrics';
import { CpuPerformanceState } from '@opentok/client';
import { isHDVideoSupported } from '../../shared/deviceCapabilities';

export type GenericCallServiceDependencies = {
  [ServiceTokens.Logger]: Logger;
  [ServiceTokens.Media]: Media;
  [ServiceTokens.Session]: Session;
  [ServiceTokens.CallActor]: CallStateMachine;
  [ServiceTokens.CommonRealtime]: CommonRealtimeService;
};

/**
 * Call Service is responsible for encapsulating call functionality
 * and managing call business logic, mostly by orchestration.
 * The service manages session, media and statemachine.
 *
 * Extends PluggableService to support plugins which can add non-core functionality
 * to this service.
 */
export abstract class GenericCallService
  extends PluggableService
  implements CallService
{
  protected readonly stateMachine: CallStateMachine;

  private readonly stateSubscription: Subscription;

  protected readonly stateSubject: BehaviorSubject<CallStateSubject>;

  protected readonly session: Session;

  protected readonly media: Media;

  protected override readonly logger: Logger;

  readonly state: Observable<CallStateSubject>;

  protected readonly realtime: CommonRealtimeService;

  protected _manualVideoQuality: ManualVideoQuality = null;

  get manualVideoQuality() {
    return this._manualVideoQuality;
  }

  constructor({
    session,
    media,
    logger,
    callActor,
    commonRealtime,
  }: GenericCallServiceDependencies) {
    super({ logger });

    this.session = session;
    this.media = media;
    this.stateMachine = callActor;
    this.logger = logger;
    this.realtime = commonRealtime;

    this.stateSubject = new BehaviorSubject<CallStateSubject>(
      this.stateMachine.getSnapshot(),
    );

    this.stateSubscription = this.stateMachine.subscribe((state) => {
      this.stateSubject.next(state);
    });

    this.state = this.stateSubject.asObservable();

    this.session.on([SessionEventType.ConnectionStatusChanged], (event) => {
      this.stateMachine.send({
        type:
          event.data?.connectionStatus === 'reconnecting'
            ? CallTransition.RECONNECTING
            : CallTransition.RECONNECTED,
      });
    });
  }

  protected getParticipantIds(): string[] {
    return Object.values(this.session.participantsMap).map(
      (participant) => participant.id,
    );
  }

  async setVideoQuality(
    value: VideoQuality,
    reason?: string,
    aspectRatio?: AspectRatio,
  ): Promise<VideoQuality> {
    let quality = value;

    if (value === 'hd' && !isHDVideoSupported()) {
      this.logger.info(
        `setVideoQuality to hd is not supported on this device. Falling back to sd.`,
      );

      quality = 'sd';
    }

    this.logger.info(
      `setVideoQuality to ${quality}: ${reason ?? 'no reason given'}`,
    );

    try {
      await this.media.changeVideoQuality(quality, aspectRatio);
    } catch (error) {
      this.logger.error(`setVideoQuality to ${quality} error`, error);
    }

    return quality;
  }

  // TODO: Add ability to capture snapshot with high resolution
  async captureSnapshot(
    participantId?: string,
    type: SupportedImageTypes = 'image/jpeg',
  ): Promise<Blob> {
    const video = this.getVideoElement(participantId);

    if (video) {
      const buffer = await getArrayBufferFromVideoElement(
        video,
        undefined,
        type,
      );

      return new Blob([buffer], { type });
    }

    throw new Error('Can not capture snapshot');
  }

  async destroy(): Promise<void> {
    this.stateSubscription.unsubscribe();
    this.stateSubject.complete();
  }

  abstract start(): Promise<unknown>;

  abstract stop(): Promise<void>;

  getMetrics(): StatsToLog | undefined {
    return this.session.getMetrics();
  }

  getCpuPerformanceState(): CpuPerformanceState | undefined {
    return this.session.getCPUPerformanceState();
  }

  abstract resume(): Promise<void>;

  private getVideoElement(
    participantId?: string,
  ): HTMLVideoElement | undefined {
    if (
      participantId === undefined ||
      this.realtime.getIdentity().id === participantId
    ) {
      return this.media.videoElement as HTMLVideoElement | undefined;
    }

    return this.session.participantsMap[participantId]?.videoElement as
      | HTMLVideoElement
      | undefined;
  }
}
